import React, { useState, useTransition } from "react";
import { useI18n } from "../../i18n";
import { getContentByLocale } from "../../content";
import { Link } from "react-router-dom";
import "./ContactForm.css";

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatBrazilPhone(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;

  const ddd = digits.slice(0, 2);
  const localNumber = digits.slice(2);

  return `(${ddd}) ${localNumber}`;
}

function isValidBrazilPhoneWithDdd(value: string) {
  const digits = onlyDigits(value).slice(0, 11);
  return digits.length === 10 || digits.length === 11;
}

type ContactFormProps = {
  submitLabel: string;
};

export function ContactForm({ submitLabel }: ContactFormProps) {
  const [isPending, startTransition] = useTransition();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [phoneLocal, setPhoneLocal] = useState("");
  const { locale } = useI18n();
  const content = getContentByLocale(locale);

  // React 19 Action-style handler
  async function handleAction(formData: FormData) {
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    const phoneInput = String(formData.get("phone") ?? "").trim();
    const phone = formatBrazilPhone(phoneInput);
    const isValidBrazilPhone = isValidBrazilPhoneWithDdd(phoneInput);
    const consent = formData.get("consent") === "on";

    if (!name || !email || !message || !isValidBrazilPhone || !consent) {
      setStatus("error");
      return;
    }

    setStatus("idle");

    startTransition(async () => {
      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            email,
            message,
            phone,
            consent,
            metadata: {
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              language: navigator.language,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
          }),
        });

        if (!response.ok) throw new Error();
        setStatus("success");
      } catch {
        setStatus("error");
      }
    });
  }

  return (
    <form action={handleAction} className="contact-form">
      <input type="text" name="name" placeholder={content.contact.namePlaceholder} aria-label={content.contact.nameLabel} autoComplete="name" className="contact-input" required />

      <div className="contact-row">
        <input type="email" name="email" placeholder={content.contact.emailPlaceholder} aria-label={content.contact.emailLabel} autoComplete="email" className="contact-input" required />

        <input
          type="tel"
          name="phone"
          placeholder={content.contact.phonePlaceholder}
          aria-label={content.contact.phoneLabel}
          autoComplete="tel"
          inputMode="numeric"
          className="contact-input"
          value={phoneLocal}
          onChange={(event) => setPhoneLocal(formatBrazilPhone(event.target.value))}
          pattern="\([0-9]{2}\) [0-9]{8,9}"
          maxLength={15}
          required
        />
      </div>

      <textarea name="message" placeholder={content.contact.messagePlaceholder} aria-label={content.contact.messageLabel} rows={5} className="contact-textarea" required />
      
      <div className="contact-consent">
        <label className="contact-checkbox-label">
          <input type="checkbox" name="consent" required />
          <span>
            {content.contact.consentLabel}{" "}
            <Link to="/privacidade" className="contact-link">
              ({content.privacy.title})
            </Link>
          </span>
        </label>
      </div>

      <button type="submit" className="contact-btn" disabled={isPending}>
        {isPending ? content.contact.sendingLabel : submitLabel}
      </button>

      {status === "success" ? (
        <p className="contact-feedback" role="status">{content.contact.successMessage}</p>
      ) : null}

      {status === "error" ? (
        <p className="contact-feedback is-error" role="alert">{content.contact.errorMessage}</p>
      ) : null}
    </form>
  );
}
